import OLSourceImageWMS from 'ol/source/ImageWMS'
import OLSourceTileWMS from 'ol/source/TileWMS'

import type OLLayerImage from 'ol/layer/Image'
import type OLLayerTile from 'ol/layer/Tile'
import type OLSourceTileImage from 'ol/source/TileImage'

import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { MapLayer } from 'config/MapLayer'

export type OLLayerWMS = OLLayerImage<OLSourceImageWMS> | OLLayerTile<OLSourceTileImage>

export class MapLayerWMS extends MapLayer {
  static autoGenerateLegendUrl (layerConf: ConfigLayerOptions, olLayer: OLLayerWMS) {
    if (!layerConf || !olLayer) {
      console.log('MapLayerWMS - autoGenerateLegendUrl - layerConf or olLayer is not defined', layerConf, olLayer)

      return
    }

    const { legend, showLegend = true, titleid } = layerConf

    if (legend || !showLegend) {
      // console.debug('MapLayerWMS - autoGenerateLegendUrl - legend is already defined or showLegend is false', titleid, legend, showLegend, layerConf)

      return
    }

    // console.debug('MapLayerWMS - autoGenerateLegendUrl - retrieving layer legend URL from OL layer', titleid, olLayer)

    // Automatically derive legend url from OL layer source
    try {
      const olSource = olLayer.getSource()

      if (!olSource) {
        console.log('MapLayerWMS - autoGenerateLegendUrl - OL layer does not have a source', titleid, olLayer)

        return
      }

      if (!(olSource instanceof OLSourceImageWMS || olSource instanceof OLSourceTileWMS)) {
        console.log('MapLayerWMS - autoGenerateLegendUrl - OL source is not an OLSourceImageWMS or OLSourceTileWMS', titleid, olSource)

        return
      }

      const legendUrl = olSource.getLegendUrl()
      if (!legendUrl) {
        console.log('MapLayerWMS - autoGenerateLegendUrl - OL source does not have a legend URL', titleid, olSource)

        return
      }

      const legendUrlObj = new URL(legendUrl)
      const legendSearchParams = legendUrlObj.searchParams

      // console.debug('MapLayerWMS - autoGenerateLegendUrl - legend URL', titleid, legendUrlObj.toString(), legendUrlObj)

      // Add missing params due to OL stripping out any params it does not know about
      const layerUrl = PoziUtils.getLayerUrl(layerConf)
      const layerSearchParams = new URL(layerUrl).searchParams
      layerSearchParams.forEach((value, key) => {
        if (!legendSearchParams.has(key)) {
          legendSearchParams.set(key, value)
        }
      })

      // Add a few extra params to make the legend look better (if not set yet)
      if (!legendSearchParams.has('LEGEND_OPTIONS')) {
        legendSearchParams.set('LEGEND_OPTIONS', 'fontAntiAliasing:true;hideEmptyRules:false') // GeoServer
      }

      if (!legendSearchParams.has('RULELABEL')) {
        legendSearchParams.set('RULELABEL', 'true') // QGIS
      }

      if (!legendSearchParams.has('TRANSPARENT')) {
        legendSearchParams.set('TRANSPARENT', 'true')
      }

      // Some WMS servers do not support PNG8 or WEBP for GetLegendGraphic requests.
      // We force the format to PNG.
      const format = legendSearchParams.get('FORMAT')
      if (format === 'image/png8' || format === 'image/webp') {
        legendSearchParams.set('FORMAT', 'image/png')
      }

      layerConf.legend = legendUrlObj.toString()

      // Some layers are styled through SLD or SLD_BODY. Those layers need to have the LAYER param
      // set explicitly in the layer config for the legend URL to work
      if (!legendSearchParams.get('LAYER')) {
        console.warn('MapLayerWMS - autoGenerateLegendUrl - legend URL is missing the required \'LAYER\' param!', titleid, layerConf.legend)
      }

      // console.debug('MapLayerWMS - generateLegendUrl - getLegendUrl', titleid, layerConf.legend, legendSearchParams)
    } catch (error) {
      console.log('MapLayerWMS - autoGenerateLegendUrl - error deriving layer legend URL from OL layer', titleid, error)
    }
  }
}
